import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import defaultApi from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  RotateCw, RotateCcw, Maximize, Check, X, Rows3, Columns3,
  Wand2, ScanLine, Sun, Contrast, Focus, Palette, RefreshCw, Loader2, Sparkles,
  Crop as CropIcon, Grid3x3, ImageIcon,
} from "lucide-react";

/**
 * CamScanner-style image editor.
 *
 * Three tabs — Crop, Perspective, Filters — all share one debounced server
 * render call to `POST {renderEndpoint}` so the on-screen preview is byte-
 * identical to what the printer receives (WYSIWYG guarantee).
 *
 * Props:
 *   open                  boolean
 *   src                   string (data-URL or object-URL of the ORIGINAL image)
 *   filename              string (used to name the returned File)
 *   aspect                number | undefined (crop tab — enforce paper ratio)
 *   renderEndpoint        string (default "/editor/render" — authed)
 *   detectCornersEndpoint string (default "/editor/detect-corners" — authed)
 *   onCancel              () => void
 *   onConfirm             (file: File, edits: object) => void
 */
const DEFAULT_EDITS = {
  rotation_deg: 0,
  corners: null,        // TL, TR, BR, BL — normalised 0..1
  filter: "none",       // none | auto | color | bw | grayscale
  brightness: 100,
  contrast: 100,
  sharpness: 100,
};

const FILTERS = [
  { key: "none",      label: "Original",  icon: ImageIcon },
  { key: "auto",      label: "Auto",      icon: Sparkles },
  { key: "color",     label: "Color",     icon: Palette },
  { key: "bw",        label: "B & W",     icon: ScanLine },
  { key: "grayscale", label: "Grayscale", icon: Grid3x3 },
];

export default function ImageEditor({
  open,
  src,
  filename = "edited.jpg",
  aspect,
  renderEndpoint = "/editor/render",
  detectCornersEndpoint = "/editor/detect-corners",
  client,
  hideCropTab = false,
  onCancel,
  onConfirm,
}) {
  const api = client || defaultApi;
  const [tab, setTab] = useState(hideCropTab ? "perspective" : "crop");
  const [edits, setEdits] = useState(DEFAULT_EDITS);

  // Crop-tab (react-easy-crop) state
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [orient, setOrient] = useState("portrait");
  const [croppedArea, setCroppedArea] = useState(null);

  // Server-rendered preview
  const [previewUrl, setPreviewUrl] = useState(null);
  const [rendering, setRendering] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [imgDims, setImgDims] = useState({ w: 0, h: 0 });
  const [busy, setBusy] = useState(false);

  const originalFileRef = useRef(null);
  const lastPreviewBlobRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const previewUrlRef = useRef(null);

  const effectiveAspect = aspect ?? (orient === "portrait" ? 1 / Math.SQRT2 : Math.SQRT2);

  // Convert incoming `src` to a File once, then learn its natural dimensions.
  useEffect(() => {
    if (!src || !open) return;
    setEdits(DEFAULT_EDITS);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedArea(null);
    setPreviewUrl(null);
    lastPreviewBlobRef.current = null;
    setTab(hideCropTab ? "perspective" : "crop");
    (async () => {
      try {
        const resp = await fetch(src);
        const blob = await resp.blob();
        originalFileRef.current = new File(
          [blob],
          filename.replace(/\.[^.]+$/, "") + ".jpg",
          { type: blob.type || "image/jpeg" },
        );
        const img = new Image();
        img.onload = () => setImgDims({ w: img.naturalWidth, h: img.naturalHeight });
        img.src = src;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("ImageEditor: could not load source image", e);
      }
    })();
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, [src, open, filename]);

  // Debounced server-render on any edits/tab change (skips crop tab).
  useEffect(() => {
    if (!open || !originalFileRef.current) return;
    if (tab === "crop") return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => renderServer(edits), 350);
  }, [edits, tab, open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-detect corners on first visit to Perspective tab.
  useEffect(() => {
    if (tab !== "perspective" || edits.corners || !originalFileRef.current) return;
    autoDetectCorners();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedArea(areaPixels);
  }, []);

  const patch = (p) => setEdits((s) => ({ ...s, ...p }));

  const renderServer = async (currentEdits) => {
    if (!originalFileRef.current) return;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setRendering(true);
    try {
      const fd = new FormData();
      fd.append("file", originalFileRef.current);
      fd.append("edits", JSON.stringify(currentEdits));
      const resp = await api.post(renderEndpoint, fd, {
        responseType: "blob",
        signal: controller.signal,
      });
      const blob = resp.data;
      lastPreviewBlobRef.current = blob;
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      const url = URL.createObjectURL(blob);
      previewUrlRef.current = url;
      setPreviewUrl(url);
    } catch (e) {
      if (!controller.signal.aborted) {
        // eslint-disable-next-line no-console
        console.error("Editor render failed", e);
      }
    } finally {
      setRendering(false);
    }
  };

  const autoDetectCorners = async () => {
    if (!originalFileRef.current) return;
    setDetecting(true);
    try {
      const fd = new FormData();
      fd.append("file", originalFileRef.current);
      const { data } = await api.post(detectCornersEndpoint, fd);
      patch({ corners: data.corners });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("auto-detect failed", e);
    } finally {
      setDetecting(false);
    }
  };

  const resetPerspective = () => patch({ corners: null });

  const rotate = (dir) => {
    const nxt = (edits.rotation_deg + (dir === "cw" ? 90 : -90) + 360) % 360;
    patch({ rotation_deg: nxt });
  };

  const resetAll = () => {
    setEdits(DEFAULT_EDITS);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  // "Confirm" behaviour depends on the active tab:
  //  - Crop tab: use the offline react-easy-crop render (client canvas).
  //  - Anywhere else: use the last server-rendered preview blob — it is by
  //    definition WYSIWYG. If no preview exists yet, fetch one on the spot.
  const confirm = async () => {
    setBusy(true);
    try {
      let outBlob = null;
      if (tab === "crop") {
        if (croppedArea && src) {
          outBlob = await renderCroppedImage(src, croppedArea, edits.rotation_deg);
        } else {
          outBlob = lastPreviewBlobRef.current;
        }
      } else {
        if (!lastPreviewBlobRef.current) {
          await renderServer(edits);
        }
        outBlob = lastPreviewBlobRef.current;
      }
      if (!outBlob) outBlob = originalFileRef.current;
      const outFile = new File(
        [outBlob],
        filename.replace(/\.[^.]+$/, "") + "_edited.jpg",
        { type: "image/jpeg" },
      );
      onConfirm?.(outFile, edits);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("confirm failed", e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel?.()}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden max-h-[92vh] flex flex-col" data-testid="image-editor">
        <DialogHeader className="px-6 pt-5 pb-3 border-b">
          <DialogTitle className="font-display flex items-center gap-2">
            <Wand2 size={18} className="text-primary" /> Edit image
            <span className="text-xs font-normal text-muted-foreground ml-1">
              Crop · Perspective · Filters — preview matches the printed page
            </span>
          </DialogTitle>
          <DialogDescription className="sr-only">
            CamScanner-style image editor with crop, perspective correction, and filters.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col min-h-0">
          <div className="px-6 pt-3 border-b">
            <TabsList data-testid="editor-tabs">
              {!hideCropTab && (
                <TabsTrigger value="crop" data-testid="editor-tab-crop">
                  <CropIcon size={13} className="mr-1.5" /> Crop
                </TabsTrigger>
              )}
              <TabsTrigger value="perspective" data-testid="editor-tab-perspective">
                <Focus size={13} className="mr-1.5" /> Perspective
              </TabsTrigger>
              <TabsTrigger value="filters" data-testid="editor-tab-filters">
                <Sparkles size={13} className="mr-1.5" /> Filters
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="crop" className="flex-1 flex flex-col m-0 min-h-0">
            {!hideCropTab && (
              <>
                <div className="relative bg-black flex-1 min-h-[380px]" data-testid="editor-crop-canvas">
                  {src && (
                    <Cropper
                      image={src}
                      crop={crop}
                      zoom={zoom}
                      rotation={edits.rotation_deg}
                      aspect={effectiveAspect}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onRotationChange={(r) => patch({ rotation_deg: r })}
                      onCropComplete={onCropComplete}
                      restrictPosition={false}
                      showGrid
                    />
                  )}
                </div>
                <CropControls
                  zoom={zoom}
                  setZoom={setZoom}
                  onRotL={() => rotate("ccw")}
                  onRotR={() => rotate("cw")}
                  rotation={edits.rotation_deg}
                  orient={orient}
                  setOrient={setOrient}
                  aspectLocked={aspect !== undefined}
                />
              </>
            )}
          </TabsContent>

          <TabsContent value="perspective" className="flex-1 flex flex-col m-0 min-h-0">
            <PerspectivePane
              src={src}
              corners={edits.corners}
              imgDims={imgDims}
              detecting={detecting}
              onCornersChange={(c) => patch({ corners: c })}
              onAutoDetect={autoDetectCorners}
              onReset={resetPerspective}
              previewUrl={previewUrl}
              rendering={rendering}
            />
          </TabsContent>

          <TabsContent value="filters" className="flex-1 flex flex-col m-0 min-h-0">
            <FiltersPane
              previewUrl={previewUrl}
              srcFallback={src}
              rendering={rendering}
              edits={edits}
              onFilter={(f) => patch({ filter: f })}
              onBrightness={(v) => patch({ brightness: v })}
              onContrast={(v) => patch({ contrast: v })}
              onSharpness={(v) => patch({ sharpness: v })}
              onResetSliders={() => patch({ brightness: 100, contrast: 100, sharpness: 100 })}
            />
          </TabsContent>
        </Tabs>

        <DialogFooter className="px-6 py-3 gap-2 flex-wrap border-t bg-secondary/30">
          <Button variant="ghost" onClick={resetAll} className="mr-auto" data-testid="editor-reset">
            <RefreshCw size={13} className="mr-1" /> Reset all
          </Button>
          <Button variant="outline" onClick={onCancel} data-testid="editor-cancel">
            <X size={13} className="mr-1" /> Cancel
          </Button>
          <Button
            onClick={confirm}
            disabled={busy || rendering}
            className="bg-brand-green hover:bg-brand-green-hover text-white"
            data-testid="editor-confirm"
          >
            {busy || rendering
              ? <><Loader2 size={13} className="mr-1 animate-spin" /> Preparing…</>
              : <><Check size={13} className="mr-1" /> Use this image</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ================================================================ */
/*                          SUB-COMPONENTS                          */
/* ================================================================ */

function CropControls({ zoom, setZoom, onRotL, onRotR, rotation, orient, setOrient, aspectLocked }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 px-6 py-4 border-t">
      <div>
        <Label className="text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
          <Maximize size={11} /> Zoom · <span className="tabular-nums">{zoom.toFixed(2)}×</span>
        </Label>
        <Slider
          min={1} max={4} step={0.05}
          value={[zoom]}
          onValueChange={(v) => setZoom(v[0])}
          className="mt-3"
          data-testid="editor-zoom-slider"
        />
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
          <RotateCw size={11} /> Rotation · <span className="tabular-nums">{rotation}°</span>
        </Label>
        <div className="flex items-center gap-2 mt-2">
          <Button variant="outline" size="sm" onClick={onRotL} data-testid="editor-rot-left" className="h-8">
            <RotateCcw size={13} className="mr-1" /> 90° L
          </Button>
          <Button variant="outline" size="sm" onClick={onRotR} data-testid="editor-rot-right" className="h-8">
            <RotateCw size={13} className="mr-1" /> 90° R
          </Button>
        </div>
      </div>
      {!aspectLocked && (
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Paper</Label>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <button
              type="button"
              onClick={() => setOrient("portrait")}
              className={`flex items-center justify-center gap-1.5 rounded-md border py-1.5 text-xs transition
                ${orient === "portrait" ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/50"}`}
              data-testid="editor-orient-portrait"
            >
              <Rows3 size={12} /> Portrait
            </button>
            <button
              type="button"
              onClick={() => setOrient("landscape")}
              className={`flex items-center justify-center gap-1.5 rounded-md border py-1.5 text-xs transition
                ${orient === "landscape" ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/50"}`}
              data-testid="editor-orient-landscape"
            >
              <Columns3 size={12} /> Landscape
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Perspective quad overlay ---------- */
function PerspectivePane({
  src, corners, imgDims, detecting, onCornersChange,
  onAutoDetect, onReset, previewUrl, rendering,
}) {
  const holderRef = useRef(null);
  const [box, setBox] = useState({ w: 1, h: 1, offX: 0, offY: 0 });
  const [dragIdx, setDragIdx] = useState(-1);

  const recomputeBox = useCallback(() => {
    const el = holderRef.current;
    if (!el || !imgDims.w) return;
    const rect = el.getBoundingClientRect();
    const ratio = imgDims.w / imgDims.h;
    let w = rect.width;
    let h = w / ratio;
    if (h > rect.height) {
      h = rect.height;
      w = h * ratio;
    }
    setBox({ w, h, offX: (rect.width - w) / 2, offY: (rect.height - h) / 2 });
  }, [imgDims.w, imgDims.h]);

  useEffect(() => {
    recomputeBox();
    const on = () => recomputeBox();
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, [recomputeBox]);

  const toScreen = (pt) => ({
    x: box.offX + pt[0] * box.w,
    y: box.offY + pt[1] * box.h,
  });
  const toNorm = (px, py) => ([
    Math.max(0, Math.min(1, (px - box.offX) / box.w)),
    Math.max(0, Math.min(1, (py - box.offY) / box.h)),
  ]);

  const handlePointerDown = (i) => (e) => {
    e.preventDefault();
    setDragIdx(i);
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const handlePointerMove = (e) => {
    if (dragIdx < 0 || !corners) return;
    const rect = holderRef.current.getBoundingClientRect();
    const [nx, ny] = toNorm(e.clientX - rect.left, e.clientY - rect.top);
    const next = corners.map((c, i) => i === dragIdx ? [nx, ny] : c);
    onCornersChange(next);
  };
  const handlePointerUp = (e) => {
    if (dragIdx < 0) return;
    setDragIdx(-1);
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
  };

  const svgPath = useMemo(() => {
    if (!corners) return "";
    const p = corners.map(toScreen);
    return `M ${p[0].x},${p[0].y} L ${p[1].x},${p[1].y} L ${p[2].x},${p[2].y} L ${p[3].x},${p[3].y} Z`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corners, box.w, box.h, box.offX, box.offY]);

  return (
    <div className="flex flex-col md:flex-row flex-1 min-h-0">
      <div className="flex-1 relative bg-neutral-900 min-h-[300px] md:min-h-[380px]" data-testid="editor-perspective-canvas">
        <div
          ref={holderRef}
          className="absolute inset-0 flex items-center justify-center touch-none"
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          {src && (
            <img
              src={src}
              alt="source"
              className="max-w-full max-h-full object-contain select-none pointer-events-none"
              onLoad={recomputeBox}
              draggable={false}
            />
          )}
          {corners && box.w > 0 && (
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              viewBox={`0 0 ${holderRef.current?.clientWidth || 100} ${holderRef.current?.clientHeight || 100}`}
              preserveAspectRatio="none"
            >
              <path d={svgPath} fill="rgba(15,98,254,0.18)" stroke="#0f62fe" strokeWidth="2" />
              {[0, 1, 2, 3].map((i) => {
                const a = toScreen(corners[i]);
                const b = toScreen(corners[(i + 1) % 4]);
                return <circle key={`mid-${i}`} cx={(a.x + b.x) / 2} cy={(a.y + b.y) / 2} r="3" fill="#0f62fe80" />;
              })}
            </svg>
          )}
          {corners && corners.map((c, i) => {
            const p = toScreen(c);
            return (
              <div
                key={`corner-${i}`}
                onPointerDown={handlePointerDown(i)}
                className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-lg cursor-grab active:cursor-grabbing touch-none"
                style={{
                  left: p.x,
                  top: p.y,
                  width: 26,
                  height: 26,
                  background: dragIdx === i ? "#22c55e" : "#0f62fe",
                }}
                data-testid={`editor-corner-${i}`}
              />
            );
          })}
          {detecting && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <div className="flex items-center gap-2 text-white text-sm">
                <Loader2 className="animate-spin" size={16} /> Detecting document edges…
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="md:w-72 border-l border-border p-4 space-y-3 bg-card overflow-y-auto">
        <div className="flex flex-col gap-2">
          <Button
            variant="outline" size="sm"
            onClick={onAutoDetect}
            disabled={detecting}
            className="w-full justify-start"
            data-testid="editor-auto-detect"
          >
            <ScanLine size={13} className="mr-2" />
            {detecting ? "Detecting…" : "Auto-detect edges"}
          </Button>
          <Button
            variant="ghost" size="sm"
            onClick={onReset}
            className="w-full justify-start text-muted-foreground"
            data-testid="editor-perspective-reset"
          >
            <RefreshCw size={13} className="mr-2" /> Clear corners
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          Drag the 4 blue dots to the corners of the document. On tap of{" "}
          <span className="font-medium">Use this image</span>, the area inside will
          be warped to a perfect rectangle — exactly as shown in the preview below.
        </p>
        <div className="rounded-md border border-border bg-neutral-100 dark:bg-neutral-900 overflow-hidden">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground px-2 py-1 border-b border-border flex items-center gap-1">
            <Sparkles size={10} /> Live preview {rendering && <Loader2 size={10} className="animate-spin ml-auto" />}
          </div>
          <div className="aspect-[3/4] flex items-center justify-center p-1">
            {previewUrl ? (
              <img src={previewUrl} alt="preview" className="max-w-full max-h-full object-contain" data-testid="editor-preview-perspective" />
            ) : (
              <p className="text-xs text-muted-foreground">Render pending…</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Filters + BCS sliders ---------- */
function FiltersPane({
  previewUrl, srcFallback, rendering, edits,
  onFilter, onBrightness, onContrast, onSharpness, onResetSliders,
}) {
  return (
    <div className="flex flex-col md:flex-row flex-1 min-h-0">
      <div className="flex-1 bg-[repeating-linear-gradient(45deg,#eee_0,#eee_10px,#f7f7f7_10px,#f7f7f7_20px)] dark:bg-neutral-950 flex items-center justify-center p-4 min-h-[300px] md:min-h-[420px] relative">
        {rendering && (
          <div className="absolute top-3 right-3 bg-black/70 text-white text-[11px] px-2 py-1 rounded-full flex items-center gap-1.5">
            <Loader2 className="animate-spin" size={11} /> Rendering
          </div>
        )}
        <img
          src={previewUrl || srcFallback}
          alt="preview"
          className="max-w-full max-h-full object-contain shadow-2xl"
          data-testid="editor-preview-filters"
        />
      </div>

      <div className="md:w-72 border-l border-border p-4 space-y-4 bg-card overflow-y-auto">
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Filter</Label>
          <div className="grid grid-cols-3 gap-2 mt-2" data-testid="editor-filter-chips">
            {FILTERS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => onFilter(key)}
                className={`flex flex-col items-center justify-center gap-1 rounded-md border py-2 text-[11px] transition
                  ${edits.filter === key
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border hover:border-primary/50 text-muted-foreground"}`}
                data-testid={`editor-filter-${key}`}
              >
                <Icon size={14} />
                <span className="leading-none">{label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-3 pt-1">
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
              <Sun size={11} /> Brightness · <span className="tabular-nums">{edits.brightness}</span>
            </Label>
            <Slider
              min={20} max={200} step={5}
              value={[edits.brightness]}
              onValueChange={(v) => onBrightness(v[0])}
              className="mt-2"
              data-testid="editor-brightness"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
              <Contrast size={11} /> Contrast · <span className="tabular-nums">{edits.contrast}</span>
            </Label>
            <Slider
              min={20} max={200} step={5}
              value={[edits.contrast]}
              onValueChange={(v) => onContrast(v[0])}
              className="mt-2"
              data-testid="editor-contrast"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
              <Focus size={11} /> Sharpness · <span className="tabular-nums">{edits.sharpness}</span>
            </Label>
            <Slider
              min={20} max={200} step={5}
              value={[edits.sharpness]}
              onValueChange={(v) => onSharpness(v[0])}
              className="mt-2"
              data-testid="editor-sharpness"
            />
          </div>
        </div>

        <Button variant="ghost" size="sm" onClick={onResetSliders}
          className="w-full text-muted-foreground" data-testid="editor-reset-sliders">
          <RefreshCw size={12} className="mr-1.5" /> Reset sliders
        </Button>
      </div>
    </div>
  );
}

/* ================================================================ */
/*   Client-side canvas crop (used by the Crop tab's confirm path)   */
/* ================================================================ */
async function renderCroppedImage(src, area, rotation) {
  const img = await loadImage(src);
  const rad = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(rad));
  const cos = Math.abs(Math.cos(rad));
  const rotW = img.width * cos + img.height * sin;
  const rotH = img.width * sin + img.height * cos;

  const off = document.createElement("canvas");
  off.width = rotW;
  off.height = rotH;
  const offCtx = off.getContext("2d");
  offCtx.translate(rotW / 2, rotH / 2);
  offCtx.rotate(rad);
  offCtx.drawImage(img, -img.width / 2, -img.height / 2);

  const MAX = 3000;
  const scale = Math.min(1, MAX / Math.max(area.width, area.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(area.width * scale);
  canvas.height = Math.round(area.height * scale);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(off, area.x, area.y, area.width, area.height, 0, 0, canvas.width, canvas.height);

  return await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.92));
}

function loadImage(src) {
  return new Promise((res, rej) => {
    const i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });
}
